import { clsx } from 'clsx';
import {
  type ComponentPropsWithRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import s from './Input.module.css';
import { useInput } from './InputRoot.js';

export interface TextareaControlProps extends ComponentPropsWithRef<'textarea'> {
  className?: string;
  id: string;
  name: string;
  maxLength?: number;
}

export const TextareaControl = ({
  className,
  defaultValue,
  maxLength,
  placeholder,
  onChange,
  ref: externalRef,
  rows = 3,
  onFocus,
  onBlur,
  ...attrs
}: TextareaControlProps) => {
  const { variant, setCount, setMaxLength } = useInput();
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(false);

  const isCounterEnabled = maxLength && maxLength > 0;

  const mergeRefs = useCallback(
    (element: HTMLTextAreaElement | null) => {
      // Update internal ref
      internalRef.current = element;

      // Update external ref if provided
      if (externalRef) {
        if (typeof externalRef === 'function') {
          externalRef(element);
        } else if (externalRef && typeof externalRef === 'object') {
          externalRef.current = element;
        }
      }
    },
    [externalRef],
  );

  useEffect(() => {
    setMaxLength(maxLength);
  }, [maxLength, setMaxLength]);

  useEffect(() => {
    if (!internalRef.current) return;
    setCount(internalRef.current.value.length);
  }, [defaultValue, setCount]);

  useEffect(() => {
    if (!internalRef.current) return;
    const initialValue = internalRef.current.value;
    setIsPlaceholderVisible(initialValue === '');
  }, [internalRef?.current?.value]);

  const handleChange: ComponentPropsWithRef<'textarea'>['onChange'] = (e) => {
    if (internalRef.current && isCounterEnabled) {
      setCount(internalRef.current.value.length);
    }

    if (onChange) {
      onChange(e);
    }
  };

  const handleFocus: ComponentPropsWithRef<'textarea'>['onFocus'] = (e) => {
    setIsPlaceholderVisible(false);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur: ComponentPropsWithRef<'textarea'>['onBlur'] = (e) => {
    const { value } = e.target;
    setIsPlaceholderVisible(value === '');

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className={clsx(s.inputWrap, className)}>
      <textarea
        className={clsx(s.textareaControl)}
        data-placeholder-shown={isPlaceholderVisible}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={handleChange}
        ref={mergeRefs}
        rows={rows}
        style={{ ...attrs.style, minHeight: `${rows * 1.25}rem` }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-invalid={variant === 'error' ? true : undefined}
        {...attrs}
      ></textarea>
    </div>
  );
};
